import { Box, Text, Heading } from '@primer/react';
import { ArrowRightIcon, CheckCircleIcon, FlameIcon } from '@primer/octicons-react';
import Reveal from './Reveal';
import { CALENDLY_URL, trackContactClick } from '../utils/links';

const included = [
  'Heuristic & UX Audit',
  'Accessibility Audit',
  'Competitive Benchmarking',
  'Prioritized Findings Report',
];

function UXAuditCallout() {
  return (
    <Box sx={{ paddingX: ['24px', '48px', '100px'], paddingBottom: [7, 8, 9] }}>
      <Reveal>
        <Box
          sx={{
            position: 'relative',
            maxWidth: '1400px',
            margin: '0 auto',
            borderRadius: '20px',
            border: '1px solid rgba(96, 165, 250, 0.35)',
            backgroundColor: 'canvas.subtle',
            backgroundImage:
              'radial-gradient(circle at 15% 20%, rgba(96, 165, 250, 0.16), transparent 55%), radial-gradient(circle at 85% 80%, rgba(167, 139, 250, 0.14), transparent 55%)',
            padding: ['32px', '48px', '64px'],
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              marginBottom: 4,
              paddingX: 3,
              paddingY: 1,
              borderRadius: '999px',
              border: '1px solid',
              borderColor: 'border.default',
              backgroundColor: 'canvas.default',
            }}
          >
            <FlameIcon size={14} fill="#facc15" />
            <Text sx={{ fontSize: 0, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'fg.muted' }}>
              Most Requested Service
            </Text>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: ['column', 'column', 'row'],
              justifyContent: 'space-between',
              gap: [5, 5, 6],
            }}
          >
            <Box sx={{ maxWidth: '640px' }}>
              <Heading
                as="h2"
                sx={{
                  margin: 0,
                  marginBottom: 3,
                  fontFamily: "'Fraunces', serif",
                  fontWeight: 300,
                  fontSize: ['1.85rem', '2.35rem', '2.75rem'],
                  lineHeight: 1.2,
                  color: 'fg.default',
                }}
              >
                Not ready for a full engagement?{' '}
                <Text
                  as="span"
                  sx={{
                    fontStyle: 'italic',
                    backgroundImage: 'linear-gradient(90deg, #8ec5ff, #c4b5fd)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Start with a UX audit.
                </Text>
              </Heading>
              <Text as="p" sx={{ margin: 0, fontSize: 2, lineHeight: 1.7, color: 'fg.muted' }}>
                A fast, expert-led review of your product's usability, accessibility, and conversion
                friction, with a prioritized report your team can act on immediately. It's the
                lowest-risk way for enterprise, fintech, AI, and SaaS teams to see exactly where
                Pegasus UX can move the needle before committing to a bigger engagement.
              </Text>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: ['auto', 'auto', '280px'] }}>
              {included.map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircleIcon size={16} fill="#8ec5ff" />
                  <Text sx={{ fontSize: 1, color: 'fg.default' }}>{item}</Text>
                </Box>
              ))}

              <Box
                as="a"
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackContactClick}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  marginTop: 2,
                  paddingX: 4,
                  paddingY: 2,
                  borderRadius: '6px',
                  border: '1px solid rgba(27, 31, 36, 0.15)',
                  backgroundColor: '#1f883d',
                  color: '#ffffff',
                  fontSize: 1,
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'background-color 0.15s ease',
                  ':hover': { backgroundColor: '#1c8139' },
                  ':active': { backgroundColor: '#197935' },
                }}
              >
                Book a UX Audit
                <ArrowRightIcon size={16} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Reveal>
    </Box>
  );
}

export default UXAuditCallout;
